import axios from 'axios'
var isDevelopment = process.env.NODE_ENV === 'development'
// Check if we're running inside a Docker container
const isDockerContainer = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'

// gettheflockout.my talks to the same backend through its own domain (nginx
// proxies /dijon-backend/ there too), so that site never calls dijondreams.com.
const isFlockoutHost = /(^|\.)gettheflockout\.my$/.test(window.location.hostname)

// Use environment variable for API URL if available, otherwise construct it
const baseURL = isFlockoutHost
  ? `https://${window.location.host}/dijon-backend/api/`
  : process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/`
  : isDevelopment
    ? `http://${isDockerContainer ? 'dijon-backend' : 'localhost'}:8019/dijon-backend/api/`
    : `https://dijondreams.com/dijon-backend/api/`

console.log('API Configuration:', {
  isDevelopment,
  isDockerContainer,
  baseURL,
  envApiUrl: process.env.REACT_APP_API_URL,
  currentHostname: window.location.hostname
})

const axiosInstance = axios.create({
    baseURL: baseURL,
    timeout: isDevelopment ? 10000000 : 1000000,
    headers: {
        'Authorization': localStorage.getItem('access_token') && (localStorage.getItem('access_token').length > 10) ? "Bearer " + localStorage.getItem('access_token') : null,
        'Content-Type': 'application/json',
        'accept': 'application/json'
    }
});


// Only ever one refresh in flight. The backend rotates refresh tokens and
// blacklists the old one (SIMPLE_JWT ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION), so firing N refreshes with the same token means
// one wins and the rest 401 against a token that is now dead — which used to
// leave the stored token permanently blacklisted and 401 every later call.
// Pages that poll several endpoints at once (the ledger) hit this every time
// the access token expired.
let refreshPromise = null;

function refreshAccessToken(refreshToken) {
    if (!refreshPromise) {
        refreshPromise = axiosInstance
            .post('/token/refresh/', { refresh: refreshToken })
            .then((response) => {
                if (!response || !response.data || !response.data.access) {
                    throw new Error('Malformed token refresh response');
                }
                localStorage.setItem('access_token', response.data.access);
                if (response.data.refresh) {
                    localStorage.setItem('refresh_token', response.data.refresh);
                }
                axiosInstance.defaults.headers['Authorization'] = "Bearer " + response.data.access;
                return response.data.access;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

function clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete axiosInstance.defaults.headers['Authorization'];
}

axiosInstance.interceptors.response.use(
    response => response,
    error => {
        const originalRequest = error.config;

        // Prevent infinite loops
        if (error && error.response && error.response.status === 401 && (originalRequest.url === '/token/refresh/' || originalRequest.url === 'token/refresh/')) {
            // The refresh token itself is dead — stop retrying with it.
            clearTokens();
            return Promise.reject(error);
        }

        if (error && error.response && error.response.status === 401 && !originalRequest._retried) {
            const refreshToken = localStorage.getItem('refresh_token');

            if (refreshToken) {
                let tokenParts;
                try {
                    tokenParts = JSON.parse(atob(refreshToken.split('.')[1]));
                } catch (e) {
                    clearTokens();
                    return Promise.reject(error);
                }

                // exp date in token is expressed in seconds, while now() returns milliseconds:
                const now = Math.ceil(Date.now() / 1000);

                if (tokenParts.exp > now) {
                    // Retry once with a fresh access token. Callers see a real
                    // rejection if the refresh fails — swallowing it here made
                    // failed writes look like successes to the UI.
                    originalRequest._retried = true;
                    return refreshAccessToken(refreshToken).then((access) => {
                        originalRequest.headers['Authorization'] = "Bearer " + access;
                        return axiosInstance(originalRequest);
                    });
                } else {
                    console.log("Refresh token is expired", tokenParts.exp, now);
                    clearTokens();
                }
            } else {
                console.log("Refresh token not available.")
            }
        }

        // specific error handling done elsewhere
        return Promise.reject(error);
    }
);

export default axiosInstance
